/**
 * Module Hierarchy Map — bbox 좌상단 책갈피 라벨 (arrayId / stringId / moduleId 표시값)
 */
import {
  readArrayDisplayNo,
  readModuleDisplayNo,
  readStringDisplayNo
} from '@/features/panel-planning/utils/panelMapFeatureProps';
import { MODULE_HIERARCHY_ROW_KIND, MODULE_HIERARCHY_FEATURE_KEYS } from './moduleHierarchyKeys';
import { resolveHierarchyPks } from './moduleHierarchySelection';
import { filterDefectsForModuleTarget } from '@/features/defect-drawer/utils/defectModuleMatch';

const K = MODULE_HIERARCHY_FEATURE_KEYS;

export const MAP_HIERARCHY_BOOKMARK_COLORS = {
  array: '#FF9800',
  string: '#1E88E5',
  module: '#00897B'
};

/**
 * @param {object} target — Drawer row / mapHighlight payload
 * @param {import('ol/Feature').default|null} [anchorFeature]
 */
export function resolveHierarchyDisplayIds(target, anchorFeature) {
  const fromFeature = anchorFeature
    ? {
        arrayId:
          anchorFeature.get?.(K.arrayDisplayId) ??
          readArrayDisplayNo(anchorFeature),
        stringId:
          anchorFeature.get?.(K.stringDisplayId) ??
          readStringDisplayNo(anchorFeature),
        moduleId:
          anchorFeature.get?.(K.moduleDisplayId) ??
          readModuleDisplayNo(anchorFeature)
      }
    : {};

  return {
    arrayId: pickDisplay(
      target?.arrayDisplayId,
      fromFeature.arrayId,
      target?.kind === MODULE_HIERARCHY_ROW_KIND.ARRAY ? target?.displayId : null
    ),
    stringId: pickDisplay(
      target?.stringDisplayId,
      fromFeature.stringId,
      target?.kind === MODULE_HIERARCHY_ROW_KIND.STRING ? target?.displayId : null
    ),
    moduleId: pickDisplay(
      target?.displayId,
      fromFeature.moduleId,
      isModuleKind(target?.kind) ? target?.displayId : null
    )
  };
}

function pickDisplay(...values) {
  for (const v of values) {
    if (v != null && v !== '') return String(v);
  }
  return null;
}

function isModuleKind(kind) {
  return kind === MODULE_HIERARCHY_ROW_KIND.MODULE || kind === MODULE_HIERARCHY_ROW_KIND.MODULE_SINGLE;
}

/**
 * 선택 tier 에 맞는 책갈피 세그먼트 (DOM 순: Array → String → Module)
 * DOM 순서 Array → String → Module, CSS column 으로 위→아래 쌓임
 * @returns {{ tier: string, text: string, color: string }[]}
 */
export function buildMapHierarchyBookmarkSegments(target, anchorFeature = null) {
  const pks = resolveHierarchyPks(target);
  if (!pks) return [];

  const ids = resolveHierarchyDisplayIds(target, anchorFeature);
  const segments = [];

  if (pks.tier === 'array' || pks.tier === 'string' || pks.tier === 'module') {
    if (ids.arrayId != null) {
      segments.push({
        tier: 'array',
        text: `Array ${ids.arrayId}`,
        color: MAP_HIERARCHY_BOOKMARK_COLORS.array
      });
    }
  }
  if (pks.tier === 'string' || pks.tier === 'module') {
    if (ids.stringId != null) {
      segments.push({
        tier: 'string',
        text: `String ${ids.stringId}`,
        color: MAP_HIERARCHY_BOOKMARK_COLORS.string
      });
    }
  }
  if (pks.tier === 'module') {
    if (ids.moduleId != null) {
      segments.push({
        tier: 'module',
        text: `Module ${ids.moduleId}`,
        color: MAP_HIERARCHY_BOOKMARK_COLORS.module
      });
    }
  }

  return segments;
}

/**
 * @param {{ tier, text, color }[]} segments
 * @returns {HTMLElement}
 */
export function createMapHierarchyBookmarkElement(segments) {
  const root = document.createElement('div');
  root.className = 'map-hierarchy-bookmark-stack';

  if (!segments?.length) {
    root.className = 'view-mode-selection-label';
    root.textContent = '';
    return root;
  }

  segments.forEach((seg, index) => {
    const tab = document.createElement('div');
    tab.className = `map-hierarchy-bookmark map-hierarchy-bookmark--${seg.tier}`;
    tab.textContent = seg.text;
    tab.style.setProperty('--bookmark-color', seg.color);
    tab.style.backgroundColor = seg.color;
    tab.style.setProperty('--bookmark-index', String(index));
    root.appendChild(tab);
  });

  return root;
}

/**
 * @param {object} target
 * @param {object[]} defects
 * @returns {object[]}
 */
export function filterDefectsForHierarchyTarget(target, defects) {
  const pks = resolveHierarchyPks(target);
  if (!pks || !defects?.length) return [];

  if (pks.tier !== 'module' || pks.moduleSeq == null) return [];

  return filterDefectsForModuleTarget(target, defects);
}

/**
 * @param {object[]} defects
 * @param {(defects: object[]) => string} [formatSummary]
 * @returns {HTMLElement|null}
 */
export function createMapHierarchyDefectListElement(defects, formatSummary) {
  if (!defects?.length) return null;

  const root = document.createElement('div');
  root.className = 'map-hierarchy-defect-summary';

  const item = document.createElement('div');
  item.className = 'map-hierarchy-defect-summary__badge';
  const label = typeof formatSummary === 'function'
    ? formatSummary(defects)
    : (defects.length > 1 ? `Defect ×${defects.length}` : 'Defect');
  item.textContent = label;
  root.appendChild(item);

  return root;
}

/**
 * @param {object} target
 * @param {import('ol/Feature').default|null} anchorFeature
 * @param {{ defects?: object[], formatDefectSummary?: (defects: object[]) => string }} [options]
 * @returns {HTMLElement}
 */
export function createMapHierarchyBookmarkOverlayElement(target, anchorFeature = null, options = {}) {
  const { defects = [], formatDefectSummary } = options;
  const root = document.createElement('div');
  root.className = 'map-hierarchy-selection-stack';

  root.appendChild(createMapHierarchyBookmarkElement(
    buildMapHierarchyBookmarkSegments(target, anchorFeature)
  ));

  const defectList = createMapHierarchyDefectListElement(defects, formatDefectSummary);
  if (defectList) {
    root.appendChild(defectList);
  }

  return root;
}

/**
 * 책갈피 앵커 좌표 — bbox 좌상단이 아니라 **가장 좌상단에 가까운 실제 모듈**의 좌상단
 * (Array가 대각선·계단식 배치면 bbox 모서리는 빈 공간이라 라벨이 허공에 뜸)
 * @param {import('ol/Feature').default[]} features
 * @returns {[number, number]|null} [x, y] 지도 좌표
 */
export function resolveBookmarkAnchorPosition(features) {
  let unionMinX = Infinity;
  let unionMaxY = -Infinity;
  const corners = [];

  (features || []).forEach(f => {
    const extent = f?.getGeometry?.()?.getExtent?.();
    if (!extent || !isFinite(extent[0])) return;
    const [minX, , , maxY] = extent;
    corners.push([minX, maxY]);
    if (minX < unionMinX) unionMinX = minX;
    if (maxY > unionMaxY) unionMaxY = maxY;
  });

  if (!corners.length) return null;

  let best = corners[0];
  let bestDist = Infinity;
  corners.forEach(([x, y]) => {
    const dx = x - unionMinX;
    const dy = unionMaxY - y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      best = [x, y];
    }
  });

  return best;
}
